import { type Ref, useCallback, useImperativeHandle, useState } from 'react';

import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import errorsValidation from '@errors/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ICreateFolderInputData } from '@services/folders/dtos/createFolder/InputData';
import FolderService from '@services/folders/FolderService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { z } from 'zod';

const formSchema = z.object({
  name: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

export interface IRefProps {
  close(): void;
  open(): void;
}

interface IProps {
  ref: Ref<IRefProps>;
}

function CreateFolderDialog({ ref }: IProps) {
  const queryClient = useQueryClient();
  const params = useParams();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
    },
  });

  const handleTrigger = useCallback(() => {
    setIsOpen((oldState) => !oldState);
    form.reset();
  }, [form]);

  const { isPending, mutateAsync } = useMutation<
    void,
    unknown,
    ICreateFolderInputData
  >({
    mutationFn: FolderService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      form.reset();
      handleTrigger();
    },
  });

  const handleCreate = (currentName: string) => {
    mutateAsync({
      name: currentName,
      folderId: params.id ? params.id : null,
      color: '#211F54',
    });
  };

  function handleSubmit(values: z.infer<typeof formSchema>) {
    handleCreate(values.name);
  }

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        handleTrigger();
      },
      close: () => {
        handleTrigger();
      },
    }),
    [handleTrigger],
  );

  return (
    <Dialog open={isOpen}>
      <DialogContent
        aria-describedby={undefined}
        className="sm:max-w-[425px] [&>button]:hidden">
        <DialogHeader>
          <DialogTitle className="text-primary">Adicionar pasta</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-primary">Nome</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="gap-y-4">
              <Button
                className="cursor-pointer"
                disabled={isPending}
                onClick={handleTrigger}
                type="button"
                variant="destructive">
                Cancelar
              </Button>
              <Button
                className="min-w-28 cursor-pointer"
                disabled={isPending}
                type="submit"
                variant="secondary">
                {isPending ? (
                  <LoaderCircle className="text-background animate-spin" />
                ) : (
                  'Criar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateFolderDialog;
